/**
 * PIO-format range parser — per /design/specs/5_game_engine_spec.md §2.
 *
 * Supports:
 *   - Pairs:               AA
 *   - Suited:              AKs, 76s
 *   - Offsuit:             AKo
 *   - Broadway (s + o):    AK         (expands to AKs + AKo)
 *   - Single hand:         AsKh
 *   - Range operators:     AA-55, AKs-A2s, KQo-KJo, JTs-J6s
 *   - Trailing-weight:     A5o:0.5
 *   - Bracketed weight:    [80]AA,22[/80]
 *
 * Notation references:
 *   - /reference/13_preflop_ranges.yaml (`:0.5` trailing weight)
 *   - /archetypes/14_fish_tendencies.yaml ([NN]...[/NN] bracketed weight)
 */

import {
  type Card,
  type Rank,
  type Suit,
  RANKS,
  RANK_VALUE,
  SUITS,
  SUIT_VALUE,
  cardsEqual,
} from '@/types/card';
import type { Combo, Range, WeightedCombo } from '@/types/range';

// =============================================================================
// Public API
// =============================================================================

/**
 * Parse a PIO-format range string into a list of weighted combos.
 *
 * Duplicate combos across the range (e.g. one item adds AsKh at 0.5, another
 * at 0.2) are merged by summing weights and clamping at 1.0.
 */
export function parseRange(input: string): Range {
  if (!input || !input.trim()) {
    throw new Error('Range string is empty');
  }

  // Step 1: lower bracketed-weight notation to per-item trailing-weight
  // notation, so all weight info lives in one place.
  const flattened = lowerBracketedWeights(input);

  // Step 2: split on commas, parse each item.
  const items = flattened
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const accumulator = new Map<string, WeightedCombo>();

  for (const item of items) {
    const { combos, weight } = parseItem(item);
    for (const combo of combos) {
      mergeCombo(accumulator, combo, weight);
    }
  }

  return Array.from(accumulator.values());
}

/** Total weighted size (sum of weights) — useful for quick sanity checks. */
export function totalWeight(range: Range): number {
  return range.reduce((sum, wc) => sum + wc.weight, 0);
}

// =============================================================================
// Step 1 — flatten bracketed weights
// =============================================================================

/**
 * Replace `[NN]item1,item2,...[/NN]` blocks with `item1:NN/100,item2:NN/100,...`
 * so subsequent parsing only sees trailing-weight notation.
 *
 * Bracketed blocks are not allowed to nest (per `/reference/14` examples).
 */
function lowerBracketedWeights(input: string): string {
  // Regex: \[(\d+(?:\.\d+)?)\] captures opening tag's number; we then walk to
  // the matching [/N] tag and replace.
  return input.replace(
    /\[(\d+(?:\.\d+)?)\]([^[]*?)\[\/\1\]/g,
    (_match, weightStr: string, contents: string) => {
      const weight = Number(weightStr) / 100;
      const items = contents
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return items.map((item) => applyTrailingWeight(item, weight)).join(',');
    },
  );
}

/**
 * Apply a weight to an item. If the item already has a `:weight` suffix,
 * multiply (compose the weights).
 */
function applyTrailingWeight(item: string, weight: number): string {
  const existing = parseTrailingWeight(item);
  if (existing === null) {
    return weight === 1 ? item : `${item}:${weight}`;
  }
  const combined = existing.weight * weight;
  return combined === 1 ? existing.body : `${existing.body}:${combined}`;
}

/** Split `item:N` into `{ body, weight }`. Returns null if no trailing weight. */
function parseTrailingWeight(item: string): { body: string; weight: number } | null {
  const idx = item.indexOf(':');
  if (idx === -1) return null;
  const body = item.slice(0, idx);
  const weight = Number(item.slice(idx + 1));
  if (!Number.isFinite(weight) || weight < 0 || weight > 1) {
    throw new Error(`Invalid weight in item "${item}" (must be in [0, 1])`);
  }
  return { body, weight };
}

// =============================================================================
// Step 2 — parse a single item
// =============================================================================

interface ParsedItem {
  combos: Combo[];
  weight: number;
}

function parseItem(rawItem: string): ParsedItem {
  // Strip trailing weight if present.
  const trailing = parseTrailingWeight(rawItem);
  const body = trailing?.body ?? rawItem;
  const weight = trailing?.weight ?? 1;

  // Range operator (e.g. AA-55, AKs-A2s).
  if (body.includes('-')) {
    return { combos: expandRangeOperator(body), weight };
  }

  // Single-hand notation (e.g. AsKh).
  if (body.length === 4) {
    return { combos: [expandSingleHand(body)], weight };
  }

  // Pair, suited, offsuit, or broadway.
  return { combos: expandShortItem(body), weight };
}

/** Expand a single 4-char hand string like 'AsKh' into a canonicalized Combo. */
function expandSingleHand(s: string): Combo {
  const a: Card = { rank: assertRank(s[0]!), suit: assertSuit(s[1]!) };
  const b: Card = { rank: assertRank(s[2]!), suit: assertSuit(s[3]!) };
  if (cardsEqual(a, b)) {
    throw new Error(`Invalid combo (same card twice): ${s}`);
  }
  return canonical(a, b);
}

/** Expand 'AA', 'AKs', 'AKo', or 'AK' (broadway). */
function expandShortItem(s: string): Combo[] {
  // Pair: 'AA', '55', etc.
  if (s.length === 2 && s[0] === s[1] && isRank(s[0]!)) {
    return pairCombos(s[0] as Rank);
  }

  // 3-char: suited or offsuit.
  if (s.length === 3) {
    const r1 = assertRank(s[0]!);
    const r2 = assertRank(s[1]!);
    const suffix = s[2];
    if (r1 === r2) {
      throw new Error(`A pair cannot be suited or offsuit: ${s}`);
    }
    const [higher, lower] = orderByRank(r1, r2);
    if (suffix === 's') return suitedCombos(higher, lower);
    if (suffix === 'o') return offsuitCombos(higher, lower);
    throw new Error(`Unknown suffix in item "${s}"`);
  }

  // 2-char broadway (e.g. 'AK', 'KQ') = suited + offsuit.
  if (s.length === 2 && isRank(s[0]!) && isRank(s[1]!)) {
    const r1 = s[0] as Rank;
    const r2 = s[1] as Rank;
    if (r1 === r2) {
      // Already covered by the pair branch above; this is a safety net.
      return pairCombos(r1);
    }
    const [higher, lower] = orderByRank(r1, r2);
    return [...suitedCombos(higher, lower), ...offsuitCombos(higher, lower)];
  }

  throw new Error(`Unknown range item: "${s}"`);
}

// =============================================================================
// Range operator (e.g. AA-55, AKs-A2s, KQo-KJo, JTs-J6s)
// =============================================================================

function expandRangeOperator(s: string): Combo[] {
  const [first, second] = s.split('-');
  if (!first || !second) {
    throw new Error(`Invalid range operator: "${s}"`);
  }

  // Pair range — e.g. AA-55.
  if (isPairItem(first) && isPairItem(second)) {
    const start = RANK_VALUE[first[0] as Rank];
    const end = RANK_VALUE[second[0] as Rank];
    if (start < end) {
      throw new Error(`Pair range must descend: "${s}"`);
    }
    const combos: Combo[] = [];
    for (let v = start; v >= end; v--) {
      const rank = rankFromValue(v);
      combos.push(...pairCombos(rank));
    }
    return combos;
  }

  // Suited range — e.g. AKs-A2s, JTs-J6s.
  if (isSuitedItem(first) && isSuitedItem(second)) {
    return expandKickerRange(first, second, 's', suitedCombos);
  }

  // Offsuit range — e.g. KQo-KJo, ATo-A8o.
  if (isOffsuitItem(first) && isOffsuitItem(second)) {
    return expandKickerRange(first, second, 'o', offsuitCombos);
  }

  // Broadway range (2-char, no suffix) — e.g. AK-AJ → AKs + AKo + AQs + AQo + AJs + AJo.
  if (isBroadwayItem(first) && isBroadwayItem(second)) {
    const r1f = first[0] as Rank;
    const r2f = first[1] as Rank;
    const r1s = second[0] as Rank;
    const r2s = second[1] as Rank;
    if (r1f !== r1s) {
      throw new Error(`Broadway range endpoints must share higher rank: "${s}"`);
    }
    const startKicker = RANK_VALUE[r2f];
    const endKicker = RANK_VALUE[r2s];
    if (startKicker < endKicker) {
      throw new Error(`Broadway range must descend in kicker: "${s}"`);
    }
    const higher = r1f;
    const combos: Combo[] = [];
    for (let v = startKicker; v >= endKicker; v--) {
      const lower = rankFromValue(v);
      if (lower === higher) continue; // skip the pair case
      combos.push(...suitedCombos(higher, lower), ...offsuitCombos(higher, lower));
    }
    return combos;
  }

  throw new Error(`Mismatched range endpoints in "${s}"`);
}

/**
 * Expand a suited or offsuit range like AKs-A2s. Endpoints must share the
 * higher rank; kicker walks from `first`'s lower down to `second`'s lower.
 */
function expandKickerRange(
  first: string,
  second: string,
  suffix: 's' | 'o',
  comboFn: (higher: Rank, lower: Rank) => Combo[],
): Combo[] {
  const r1f = first[0] as Rank;
  const r2f = first[1] as Rank;
  const r1s = second[0] as Rank;
  const r2s = second[1] as Rank;
  if (r1f !== r1s) {
    throw new Error(
      `Range endpoints must share higher rank (got "${first}" and "${second}")`,
    );
  }
  const startKicker = RANK_VALUE[r2f];
  const endKicker = RANK_VALUE[r2s];
  if (startKicker < endKicker) {
    throw new Error(`Range must descend in kicker (got "${first}-${second}")`);
  }
  const higher = r1f;
  const combos: Combo[] = [];
  for (let v = startKicker; v >= endKicker; v--) {
    const lower = rankFromValue(v);
    if (lower === higher) continue; // skip pair case in the middle of a suited range
    const expanded = comboFn(higher, lower);
    // Suppress the `s`/`o` suffix typecheck warning.
    void suffix;
    combos.push(...expanded);
  }
  return combos;
}

// =============================================================================
// Combo generators
// =============================================================================

function pairCombos(rank: Rank): Combo[] {
  const combos: Combo[] = [];
  for (let i = 0; i < SUITS.length; i++) {
    for (let j = i + 1; j < SUITS.length; j++) {
      const a: Card = { rank, suit: SUITS[i]! };
      const b: Card = { rank, suit: SUITS[j]! };
      combos.push(canonical(a, b));
    }
  }
  return combos; // 6 combos
}

function suitedCombos(higher: Rank, lower: Rank): Combo[] {
  return SUITS.map((suit) =>
    canonical({ rank: higher, suit }, { rank: lower, suit }),
  );
}

function offsuitCombos(higher: Rank, lower: Rank): Combo[] {
  const combos: Combo[] = [];
  for (const sa of SUITS) {
    for (const sb of SUITS) {
      if (sa === sb) continue;
      combos.push(canonical({ rank: higher, suit: sa }, { rank: lower, suit: sb }));
    }
  }
  return combos; // 12 combos
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Canonical combo order per /design/specs/5_game_engine_spec.md §1.3:
 *   - Higher-rank card first.
 *   - Within the same rank, spade-first (s < h < d < c).
 *
 * This is deliberately NOT just `compareCards` — that function is for general
 * card comparison where suits are equal-value (showdown evaluation should not
 * care about suit). Here we specifically want a tiebreak.
 */
function canonical(a: Card, b: Card): Combo {
  const rankDiff = RANK_VALUE[a.rank] - RANK_VALUE[b.rank];
  if (rankDiff > 0) return [a, b];
  if (rankDiff < 0) return [b, a];
  // Same rank: smaller SUIT_VALUE (s < h < d < c) wins → that card first.
  return SUIT_VALUE[a.suit] <= SUIT_VALUE[b.suit] ? [a, b] : [b, a];
}

function comboKey(combo: Combo): string {
  return `${combo[0].rank}${combo[0].suit}${combo[1].rank}${combo[1].suit}`;
}

function mergeCombo(acc: Map<string, WeightedCombo>, combo: Combo, weight: number): void {
  if (weight <= 0) return;
  const key = comboKey(combo);
  const existing = acc.get(key);
  if (existing) {
    existing.weight = Math.min(1, existing.weight + weight);
  } else {
    acc.set(key, { combo, weight });
  }
}

function isRank(c: string): c is Rank {
  return (RANKS as readonly string[]).includes(c);
}

function isSuit(c: string): c is Suit {
  return (SUITS as readonly string[]).includes(c);
}

function assertRank(c: string): Rank {
  if (!isRank(c)) throw new Error(`Invalid rank: ${c}`);
  return c;
}

function assertSuit(c: string): Suit {
  if (!isSuit(c)) throw new Error(`Invalid suit: ${c}`);
  return c;
}

function orderByRank(r1: Rank, r2: Rank): [Rank, Rank] {
  return RANK_VALUE[r1] >= RANK_VALUE[r2] ? [r1, r2] : [r2, r1];
}

function rankFromValue(v: number): Rank {
  const rank = RANKS.find((r) => RANK_VALUE[r] === v);
  if (!rank) throw new Error(`No rank for value ${v}`);
  return rank;
}

function isPairItem(s: string): boolean {
  return s.length === 2 && s[0] === s[1] && isRank(s[0]!);
}

function isSuitedItem(s: string): boolean {
  return s.length === 3 && isRank(s[0]!) && isRank(s[1]!) && s[2] === 's' && s[0] !== s[1];
}

function isOffsuitItem(s: string): boolean {
  return s.length === 3 && isRank(s[0]!) && isRank(s[1]!) && s[2] === 'o' && s[0] !== s[1];
}

function isBroadwayItem(s: string): boolean {
  return s.length === 2 && isRank(s[0]!) && isRank(s[1]!) && s[0] !== s[1];
}
